import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface MfaContextType {
  /** True when MFA is required but not yet verified this session */
  mfaPending: boolean;
  /** True when a mandatory-role user has no MFA configured yet */
  mfaSetupRequired: boolean;
  /** The MFA method (email/totp) — used to decide whether to send code */
  mfaMethod: "email" | "totp" | null;
  /** Mark MFA as verified for this session */
  markVerified: () => void;
  /** Reset MFA state (e.g. on sign out) */
  resetMfa: () => void;
  /** True while checking MFA status */
  checking: boolean;
}

const MfaContext = createContext<MfaContextType>({
  mfaPending: false,
  mfaSetupRequired: false,
  mfaMethod: null,
  markVerified: () => {},
  resetMfa: () => {},
  checking: false,
});

export const useMfa = () => useContext(MfaContext);

const SESSION_KEY = "vazoul-mfa-verified";

export function MfaProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [mfaPending, setMfaPending] = useState(false);
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const [mfaMethod, setMfaMethod] = useState<"email" | "totp" | null>(null);
  const [checking, setChecking] = useState(false);
  const [checked, setChecked] = useState(false);

  const checkMfaRequired = useCallback(async () => {
    if (!user) {
      setMfaPending(false);
      setMfaSetupRequired(false);
      setMfaMethod(null);
      setChecked(true);
      return;
    }

    // If already verified this session, skip
    const verified = sessionStorage.getItem(SESSION_KEY);
    if (verified === user.id) {
      setMfaPending(false);
      setMfaSetupRequired(false);
      setChecked(true);
      return;
    }

    setChecking(true);
    try {
      // manage-mfa requires a live user JWT. If the stored session is stale
      // (session_not_found / expired), skip the call instead of surfacing a 401.
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) {
        setMfaPending(false);
        setMfaSetupRequired(false);
        setMfaMethod(null);
        return;
      }

      // Use check_login to determine MFA status WITHOUT sending any code
      const { data, error } = await supabase.functions.invoke("manage-mfa", {
        headers: { Authorization: `Bearer ${accessToken}` },
        body: { action: "check_login" },
      });

      if (error) throw error;


      if (!data?.mfa_required) {
        // No MFA needed
        sessionStorage.setItem(SESSION_KEY, user.id);
        setMfaPending(false);
        setMfaSetupRequired(false);
        setMfaMethod(null);
      } else if (data?.mfa_setup_required) {
        // Mandatory role, no MFA configured — force setup
        setMfaPending(true);
        setMfaSetupRequired(true);
        setMfaMethod(null);
      } else {
        // MFA enabled, need verification
        setMfaPending(true);
        setMfaSetupRequired(false);
        setMfaMethod(data.method || null);
      }
    } catch {
      // On error, fail-open
      sessionStorage.setItem(SESSION_KEY, user.id);
      setMfaPending(false);
      setMfaSetupRequired(false);
      setMfaMethod(null);
    } finally {
      setChecking(false);
      setChecked(true);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && user && !checked) {
      checkMfaRequired();
    }
    if (!user) {
      setChecked(false);
      setMfaPending(false);
      setMfaSetupRequired(false);
      setMfaMethod(null);
      sessionStorage.removeItem(SESSION_KEY);
    }
  }, [user, authLoading, checked, checkMfaRequired]);

  const markVerified = useCallback(() => {
    if (user) {
      sessionStorage.setItem(SESSION_KEY, user.id);
    }
    setMfaPending(false);
    setMfaSetupRequired(false);
  }, [user]);

  const resetMfa = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
    setMfaPending(false);
    setMfaSetupRequired(false);
    setMfaMethod(null);
    setChecked(false);
  }, []);

  return (
    <MfaContext.Provider value={{ mfaPending, mfaSetupRequired, mfaMethod, markVerified, resetMfa, checking }}>
      {children}
    </MfaContext.Provider>
  );
}
